import moment from 'moment'
import { defineStore } from 'pinia'
import type { User } from '@/interfaces/user'
import { useApiRequest } from '@/vue/api/useApiRequest'
import { USER_PROFILE_URL } from '@/vue/helpers/apiURLS'
import {
    type Permission,
    PERMISSION_READ_COMPANIES, PERMISSION_READ_COMPANY_DATA, PERMISSION_READ_PORTFOLIOS,
    PERMISSION_WRITE_PORTFOLIOS,
} from '@/vue/helpers/permissions'
import { ROLE_ADMIN, ROLE_DATA_REQUEST_CONTRIBUTOR, ROLE_SUPER_ADMIN } from '@/vue/helpers/roles'
import { clearAccessToken, getAccessToken, setAccessToken } from '@/vue/helpers/tokenStorage'
import { usePostHog } from '../composables/use-post-hog'

function hasPermission(user: User, permission: string): boolean {
    return user.roles
        .flatMap(role => role.permissions)
        .some(p => p.name === permission)
}

const { posthog } = usePostHog()

export const useUserStore = defineStore('userStore', {
    state: (): { userData: User | null, accessToken: string | null } => {
        return {
            userData: null,
            accessToken: null,
        }
    },
    getters: {
        user(): User {
            return this.userData as unknown as User
        },
        isSuperAdmin(): boolean {
            if (this.userData === null) {
                return false
            }

            if (!this.userData.roles) {
                return false
            }

            return this.userData.roles.some(role => role.name === ROLE_SUPER_ADMIN)
        },
        isAdmin(): boolean {
            if (this.userData === null) {
                return false
            }

            if (!this.userData.roles) {
                return false
            }

            return this.userData.roles.some(role => role.name === ROLE_ADMIN)
        },
        isDataRequestContributor(): boolean {
            if (this.userData === null) {
                return false
            }

            if (!this.userData.roles) {
                return false
            }

            return this.userData.roles.some(role => role.name === ROLE_DATA_REQUEST_CONTRIBUTOR)
        },
        /**
         * @deprecated use companyId instead
         */
        getUserCompany(): any {
            return this.userData?.company_id
        },
        companyId(): number | undefined {
            return this.userData?.company_id ?? undefined
        },
        hasEditPermission(): boolean {
            if (this.userData === null) {
                return false
            }

            return hasPermission(this.userData, PERMISSION_WRITE_PORTFOLIOS)
        },
        canReadCompanies(): boolean {
            if (!this.userData) {
                return false
            }
            return hasPermission(this.userData, PERMISSION_READ_COMPANIES)
        },
        hasAnyCompanyAccess(): boolean {
            if (!this.userData) {
                return false
            }
            return hasPermission(this.userData, PERMISSION_READ_COMPANIES)
                || hasPermission(this.userData, PERMISSION_READ_COMPANY_DATA)
                || (this.userData.consultant_admin_company_ids?.length ?? 0) > 0
        },
        canAccessCollectionMode(): boolean {
            if (this.isSuperAdmin || this.isAdmin) {
                return true
            }
            if (this.hasEditPermission) {
                return true
            }
            if ((this.userData?.consultant_admin_company_ids?.length ?? 0) > 0) {
                return true
            }
            const hasWritablePortfolio = this.userData?.portfolios.some(
                p => p.pivot?.can_write === true,
            )
            if (hasWritablePortfolio) {
                return true
            }
            const hasWritableAsset = this.userData?.assets.some(
                a => a.pivot?.can_write === true,
            )
            return hasWritableAsset ?? false
        },
    },
    actions: {
        initializeFromStorage(): void {
            this.accessToken = getAccessToken()
        },
        postHogIdentify(): void {
            const userId = this.userData?.id

            if (!userId) {
                posthog.reset()
                posthog.opt_in_capturing()
                return
            }

            if (this.isSuperAdmin || this.userData?.is_impersonating) {
                posthog.opt_out_capturing()
                posthog.reset()
                return
            }

            posthog.opt_in_capturing()
            posthog.identify(String(userId))
        },
        async fetchUser(force = true): Promise<void> {
            try {
                if (!force && this.userData !== null) {
                    this.postHogIdentify()
                    return
                }
                const response = await useApiRequest<{ data: { user: User } }>(USER_PROFILE_URL)
                this.userData = response.data.value!.data.user
                this.postHogIdentify()
            } catch (error) {
                // TODO handle error correctly toast or something
            }
        },
        /**
         * @deprecated getter user should be used instead
         */
        authUser(): User {
            return this.userData as unknown as User
        },
        canAccessPortfolio(portfolioId: number, requireWriteAccess: boolean = false, includeVisibleThroughAssets: boolean = false): boolean {
            if (this.canAccessCompanyPortfolio(portfolioId)) {
                return true
            }

            if (this.canAccessUserPortfolio(portfolioId, requireWriteAccess)) {
                return true
            }

            if (includeVisibleThroughAssets && this.canAccessPortfolioThroughAssets(portfolioId, requireWriteAccess)) {
                return true
            }

            return false
        },
        canAccessCompanyPortfolio(portfolioId: number): boolean {
            if (this.userData === null) {
                return false
            }

            // Super admins can access any company portfolio
            if (this.hasPermission(PERMISSION_READ_COMPANIES)) {
                return true
            }

            // Admins can access all portfolios under their own company
            if (this.hasPermission(PERMISSION_READ_COMPANY_DATA)) {
                if (this.userData.company_portfolios_ids.some(companyPortfolioId => companyPortfolioId === portfolioId)) {
                    return true
                }
            }

            // Consultant admins can access portfolios from administered companies
            return this.userData.consultant_admin_portfolio_ids?.includes(portfolioId) ?? false
        },
        canAccessUserPortfolio(portfolioId: number, requireWriteAccess: boolean = false): boolean {
            if (this.userData === null) {
                return false
            }

            if (!this.hasPermission(PERMISSION_READ_PORTFOLIOS)) {
                return false
            }

            const userPortfolio = this.userData.portfolios.find(userPortfolio => userPortfolio.id === portfolioId)

            if (userPortfolio !== undefined) {
                return !requireWriteAccess || userPortfolio.pivot?.can_write === true
            }

            return false
        },
        canAccessPortfolioThroughAssets(portfolioId: number, writeOnly: boolean = false): boolean {
            if (this.userData === null) {
                return false
            }

            const userAssets = this.userData.assets.filter(userAsset => userAsset.portfolio_id === portfolioId)

            if (userAssets.length > 0) {
                if (!writeOnly) {
                    return true
                }

                return userAssets.some(userAsset => userAsset.pivot?.can_write === writeOnly)
            }

            return false
        },
        isConsultantAdminFor(companyId: number): boolean {
            return this.userData?.consultant_admin_company_ids?.includes(companyId) ?? false
        },
        canForCompany(permission: Permission, companyId: number): boolean {
            if (this.isSuperAdmin) {
                return true
            }
            return (this.hasPermission(permission) && this.userData?.company_id === companyId)
                || this.isConsultantAdminFor(companyId)
        },
        hasPermission(permission: Permission): boolean {
            if (this.userData === null) {
                return false
            }

            return hasPermission(this.userData, permission)
        },
        setAccessToken(accessToken: string): void {
            this.accessToken = accessToken
            setAccessToken(accessToken)
        },
        removeLoggedInUser(): void {
            this.accessToken = null
            clearAccessToken()
            this.postHogIdentify()
        },
        dateFormatter(date?: string): string | null {
            if (!date || moment(date).isValid() === false) {
                return null
            }

            const userDateFormat = this.user.units?.date
                ?.replace('m', 'MM')
                ?.replace('d', 'DD')
                ?.replace('Y', 'YYYY')

            return moment(date).format(userDateFormat ?? 'YYYY-MM-DD')
        },
    },
})
